import React from 'react'
import { Button, Divider, Link } from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import Tags, { Tag, Label } from '../offer-tags/offerTags'
import { getOfferTags, getOfferLabels } from '../lib/utilities'
import { getOfferMetaText } from '../utils/get-offer-details'
import { Breakpoint } from '../lib/useBreakpoint'
import { OfferDetailType, UrlQueryParamsType } from '../types/StorePageType'
import CheckmarkCopyButton from '../checkmark-copy-button/CheckmarkCopyButton'
import { getOutClickUrl } from '../utils/get-out-click-url'
import {
  COUPON_TYPE,
  REVEALED_COUPON_TYPE,
  SHOPPING_MERCHANT_CODE_COPY_PRESSED,
  SHOPPING_MERCHANT_CODE_PRESSED,
  SHOPPING_MERCHANT_DEAL_PRESSED
} from '../lib/client-constants'
import getAffiliateLink from '../utils/get-affiliate-link'
import { sendFptiClick } from '../lib/fpti'
import useWorldReady, { FormatMessage } from '../lib/useWorldReady'

interface OfferDetailsTitleProps {
  offerData: OfferDetailType
  offerTypeBadge: string
  formatMessage: FormatMessage
}

interface OfferDetailsProps {
  offerData: OfferDetailType
  offerType: string
  breakpoint: Breakpoint
  urlProperties: UrlQueryParamsType
  encryptedAccountNumber?: string
}

const OfferDetailsTitle = ({
  offerData,
  offerTypeBadge,
  formatMessage
}: OfferDetailsTitleProps): React.JSX.Element => {
  return (
    <div
      data-cy="offer_details__side_title"
      className="offer-details__short-title"
    >
      <span>{getOfferMetaText(offerData, formatMessage)}</span>
      <span className="offer-details__badge">{offerTypeBadge}</span>
    </div>
  )
}

const OfferDetails = ({
  offerData,
  offerType,
  breakpoint,
  urlProperties,
  encryptedAccountNumber
}: OfferDetailsProps): React.JSX.Element | null => {
  const { formatMessage } = useWorldReady()
  const { isMobile } = breakpoint
  const offerTags: Tag[] = getOfferTags(offerData, breakpoint, formatMessage)
  const labelTags: Label[] = getOfferLabels(
    offerData,
    breakpoint,
    formatMessage
  )

  if (!offerData) {
    return null
  }

  let offerTypeBadge = formatMessage('main.storePage.type.deal')

  const handleCopyToClipboardButtonClick = (
    event: React.MouseEvent<HTMLButtonElement> &
      React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.stopPropagation()
    sendFptiClick({
      event_name: SHOPPING_MERCHANT_CODE_COPY_PRESSED,
      offer_id: offerData.offerId,
      component: 'shoplistnodeweb',
      product: 'shopping'
    })
  }

  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement> &
      React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.stopPropagation()
    const targetUrl = offerData.offerUrl
      ? offerData.offerUrl
      : offerData.storeUrl
    const affiliateTagUrl = getAffiliateLink({
      honeyStoreId: offerData.storeId,
      fptiSessionId: offerData.fptiSessionId,
      turl: targetUrl,
      encryptedAccountNumber
    })
    if (urlProperties && urlProperties.isModalOpen === 'true') {
      window.open(affiliateTagUrl, '_blank')
    } else {
      const buttonUrl = getOutClickUrl(
        offerData.offerId,
        offerType,
        window.location.href,
        affiliateTagUrl // temporary property for affiliate link frontend fix story: DTSHOPATMS-1503
      )
      window.open(buttonUrl, '_blank')
      window.location.href = affiliateTagUrl
    }
  }

  const handleDealClick = (
    event: React.MouseEvent<HTMLButtonElement> &
      React.MouseEvent<HTMLAnchorElement>
  ) => {
    handleButtonClick(event)
    sendFptiClick({
      event_name: SHOPPING_MERCHANT_DEAL_PRESSED,
      offer_id: offerData.offerId
    })
  }

  const handleCouponClick = (
    event: React.MouseEvent<HTMLButtonElement> &
      React.MouseEvent<HTMLAnchorElement>
  ) => {
    handleButtonClick(event)
    sendFptiClick({
      event_name: SHOPPING_MERCHANT_CODE_PRESSED,
      offer_id: offerData.offerId
    })
  }

  let offerCtaButton = (
    <Button
      className="offer-details__button-width ppvx_btn ppvx_btn--secondary"
      size={isMobile ? 'sm' : undefined}
      onClick={handleDealClick}
      secondary
    >
      <Message id="main.storePage.offerDetails.shopNow" />
    </Button>
  )

  if (offerType === COUPON_TYPE) {
    offerTypeBadge = formatMessage('main.storePage.type.discount')
    offerCtaButton = (
      <Button
        className="offer-details__button-width ppvx_btn ppvx_btn--secondary"
        size={isMobile ? 'sm' : undefined}
        onClick={handleCouponClick}
        secondary
      >
        <Message id="main.storePage.offerDetails.copyCode" />
      </Button>
    )
  } else if (offerType === REVEALED_COUPON_TYPE) {
    offerTypeBadge = formatMessage('main.storePage.type.discount')
    offerCtaButton = (
      <CheckmarkCopyButton
        onClick={handleCopyToClipboardButtonClick}
        secondary
        buttonText={offerData.couponCode!}
        clipboard={offerData.couponCode!}
        buttonStyles="offer-details__button-width ppvx_btn ppvx_btn--secondary"
        breakpoint={breakpoint}
      />
    )
  }

  const cardContent = (
    <div className="offer-details__content">
      <div className="offer-details__offer-deal">
        <OfferDetailsTitle
          offerData={offerData}
          offerTypeBadge={offerTypeBadge}
          formatMessage={formatMessage}
        />
        <div className="offer-details__desc">
          {offerData.description ? (
            <div className="offer-details__title">
              <p>{offerData.description}</p>
            </div>
          ) : (
            ''
          )}
          {labelTags.length || offerTags.length ? (
            <div className="offer-details__labels">
              <div className="offer-details__tags">
                {labelTags.map((item, i) => (
                  <label key={item.text + i}>{item.text}</label>
                ))}
              </div>
              <Tags className="offer-details__meta" tags={offerTags} />
            </div>
          ) : (
            ''
          )}
          <div className="offer-details__offer-button">{offerCtaButton}</div>
        </div>
      </div>
      <Divider className="offer-details__divider" />
    </div>
  )

  if (offerType === COUPON_TYPE) {
    return (
      <Link
        as="div"
        className="offer-details ppvx_link"
        role="button"
        tabIndex="0"
        onClick={handleCouponClick}
        secondary
      >
        {cardContent}
      </Link>
    )
  } else if (offerType === REVEALED_COUPON_TYPE) {
    return (
      <Link
        as="div"
        className="offer-details ppvx_link"
        role="link"
        tabIndex="0"
        onClick={handleCouponClick}
        secondary
      >
        {cardContent}
      </Link>
    )
  }

  return (
    <Link
      as="div"
      className="offer-details ppvx_link"
      role="link"
      tabIndex="0"
      onClick={handleDealClick}
      secondary
    >
      {cardContent}
    </Link>
  )
}

export default OfferDetails
